import React, { useEffect, useState } from 'react';
import { ListTodo, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { messengerApi } from '../lib/messengerApi';
import type { ChatMessage, ParticipantBrief } from '../types';

interface Props {
  message: ChatMessage | null;
  participants: ParticipantBrief[];
  onClose: () => void;
}

type Priority = 'none' | 'urgent_important' | 'not_urgent_important' | 'urgent_not_important' | 'not_urgent_not_important';

// Task #1660 — dialog "Crea task dal messaggio". Parità monolite:
// titolo (pre-popolato dai primi 100 char), assegnatario, priorità Eisenhower,
// scadenza opzionale.
export function CreateTaskDialog({ message, participants, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState<Priority>('none');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!message) return;
    setTitle((message.content || '').slice(0, 100) || 'Task da chat');
    setAssignee('');
    setPriority('none');
    setDueDate('');
  }, [message]);

  const submit = async () => {
    if (!message || !title.trim()) return;
    setSaving(true);
    try {
      const body: { title: string; priority: string; due_date?: string; assignee_id?: string } = {
        title: title.trim(),
        priority,
      };
      if (dueDate) body.due_date = dueDate;
      if (assignee) body.assignee_id = assignee;
      const res = await messengerApi.createTaskFromMessage(message.id, body);
      if (res.success) onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!message} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-blue-500" /> Crea task dal messaggio
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm font-medium">Titolo</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-sm font-medium">Assegna a</Label>
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Non assegnato</option>
              {participants.map((p) => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Priorità</Label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
                className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="none">Nessuna</option>
                <option value="urgent_important">Urgente + Importante</option>
                <option value="not_urgent_important">Importante</option>
                <option value="urgent_not_important">Urgente</option>
                <option value="not_urgent_not_important">Non urgente</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium">Scadenza</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => void submit()} disabled={!title.trim() || saving} className="bg-blue-600 hover:bg-blue-700">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Crea task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
